import { FlightLevel, FlightLevel_Unit, FlightLevelBand } from 'app/model/CORE';

export default class FlightLevelUtils {
    public static readonly FEET_PER_METRE = 3.28084;
    public static readonly METRE_PER_FOOT = 0.3048;
    public static readonly FEET_PER_FLIGHT_LEVEL = 100;
    public static readonly METRES_PER_FLIGHT_LEVEL =
        FlightLevelUtils.FEET_PER_FLIGHT_LEVEL * FlightLevelUtils.METRE_PER_FOOT;

    /**
     * As defined in the B2B model : Indicates the highest flight level, which corresponds to level F999.
     */
    public static readonly UNLIMITED_VALUE = 999;

    /**
     * As defined in the B2B model : Indicates the ground flight level, i.e. 0.
     */
    public static readonly ON_GROUND_VALUE = 0;

    /**
     * Static instance representing 'on the ground' FlightLevel, i.e. FL0
     */
    public static readonly ON_GROUND_LEVEL: FlightLevel = FlightLevelUtils.newFlightLevel(
        FlightLevelUtils.ON_GROUND_VALUE,
    );

    /**
     * Static instance representing an 'unlimited' FlightLevel, i.e. FL999.
     */
    public static readonly UNLIMITED_LEVEL = FlightLevelUtils.newFlightLevel(FlightLevelUtils.UNLIMITED_VALUE);

    /**
     * The step size between two normal (controller issued) FLs
     * e.g. FL100, FL110
     */
    public static readonly NOMINAL_LEVEL_STEP = 10;

    static newFlightLevel(value: number, unit: FlightLevel_Unit = FlightLevel_Unit.FLIGHT_LEVEL): FlightLevel {
        return { unit: unit, value: value };
    }

    static copy(level: FlightLevel): FlightLevel {
        return level ? { unit: level.unit, value: level.value } : null;
    }

    /**
     * Return the flight level, expressed in theUnit.
     *
     */
    static getValueInUnit(level: FlightLevel, theUnit: FlightLevel_Unit): number {
        if (!level) return null;
        switch (theUnit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return FlightLevelUtils.getFeetValue(level);
            case FlightLevel_Unit.ALTITUDE_METRES:
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return FlightLevelUtils.getMeterValue(level);
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return FlightLevelUtils.getLevelValue(level);
        }
        // throw "Unexpected unit " + theUnit; // should never happen
        return null;
    }

    /**
     * Return the flight level, expressed in feet.
     *
     */
    static getFeetValue(level: FlightLevel): number {
        if (!level) return null;
        switch (level.unit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return level.value;
            case FlightLevel_Unit.ALTITUDE_METRES:
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return level.value * FlightLevelUtils.FEET_PER_METRE;
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return level.value * FlightLevelUtils.FEET_PER_FLIGHT_LEVEL;
        }
        return null;
    }

    /**
     * Return the flight level, expressed in metres.
     */
    static getMeterValue(level: FlightLevel): number {
        if (!level) return null;
        switch (level.unit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return level.value * FlightLevelUtils.METRE_PER_FOOT;
            case FlightLevel_Unit.ALTITUDE_METRES:
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return level.value;
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return level.value * FlightLevelUtils.METRES_PER_FLIGHT_LEVEL;
        }
        return null; // should never happen
    }

    static getLevelValue(level: FlightLevel): number {
        if (!level) return null;
        switch (level.unit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return Math.trunc(level.value / FlightLevelUtils.FEET_PER_FLIGHT_LEVEL);
            case FlightLevel_Unit.ALTITUDE_METRES:
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return Math.trunc(level.value / FlightLevelUtils.METRES_PER_FLIGHT_LEVEL);
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return level.value;
        }
        return null; // should never happen
    }

    /**
     * Return a FlightLevel, increased/decreased by a number of nominal flight levels (1000ft).
     * The original FlightLevel's unit of measure is preserved.
     * Note : the method will guard against -ive FLs, with a lower bound returned at FL0.
     */
    static addLevels(level: FlightLevel, numOfLevels: number): FlightLevel {
        switch (level.unit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return {
                    value: Math.max(
                        0,
                        level.value +
                            FlightLevelUtils.FEET_PER_FLIGHT_LEVEL * FlightLevelUtils.NOMINAL_LEVEL_STEP * numOfLevels,
                    ),
                    unit: level.unit,
                };
            case FlightLevel_Unit.ALTITUDE_METRES:
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return {
                    value: Math.max(
                        0,
                        level.value +
                            FlightLevelUtils.METRES_PER_FLIGHT_LEVEL *
                                FlightLevelUtils.NOMINAL_LEVEL_STEP *
                                numOfLevels,
                    ),
                    unit: level.unit,
                };
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return {
                    value: Math.max(0, level.value + FlightLevelUtils.NOMINAL_LEVEL_STEP * numOfLevels),
                    unit: level.unit,
                };
        }
        return null; // should never happen
    }

    /**
     * Return a FlightLevel that is increased by the given number of feet.
     * The returned FlightLevel will use the ALTITUDE_FEET unit of measure.
     * Note : the method will guard against -ive FLs, with a lower bound returned at 0.
     */
    static addFeet(level: FlightLevel, feet: number): FlightLevel {
        switch (level.unit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return { value: Math.max(0, level.value + feet), unit: level.unit };
            case FlightLevel_Unit.ALTITUDE_METRES:
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return {
                    value: Math.max(0, level.value * FlightLevelUtils.FEET_PER_METRE + feet),
                    unit: FlightLevel_Unit.ALTITUDE_FEET,
                };
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return {
                    value: Math.max(0, level.value * FlightLevelUtils.FEET_PER_FLIGHT_LEVEL + feet),
                    unit: FlightLevel_Unit.ALTITUDE_FEET,
                };
        }
        return null; // should never happen
    }

    static toString(level: FlightLevel): string {
        if (!level) return null;
        switch (level.unit) {
            case FlightLevel_Unit.ALTITUDE_FEET:
                return level.value + 'ft';
            case FlightLevel_Unit.ALTITUDE_METRES:
                return level.value + 'm';
            case FlightLevel_Unit.FLIGHT_LEVEL:
                return 'FL' + level.value;
            case FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES:
                return level.value + 'sm';
        }
        return '??';
    }

    static toFlightLevelBandString(level: FlightLevelBand): string {
        if (!level) return null;
        return '[' + this.toString(level.min) + ' - ' + this.toString(level.max) + ']';
    }

    /**
     * Parse flight level strings of the form, FL100, 1000ft, 1000m, 100sm
     * See https://stackoverflow.com/questions/14667713/typescript-converting-a-string-to-a-number for a discussion on how best to
     * convert strings to numbers in typescript (i.e. parseInt vs Number)
     *
     */
    static parseFlightLevel(str: string): FlightLevel {
        if (str.startsWith('FL')) {
            return { value: Number(str.substring(2)), unit: FlightLevel_Unit.FLIGHT_LEVEL };
        } else if (str.endsWith('ft')) {
            return { value: Number(str.substring(0, str.length - 2)), unit: FlightLevel_Unit.ALTITUDE_FEET };
        } else if (str.endsWith('m')) {
            return { value: Number(str.substring(0, str.length - 1)), unit: FlightLevel_Unit.ALTITUDE_METRES };
        } else if (str.endsWith('sm')) {
            return {
                value: Number(str.substring(0, str.length - 2)),
                unit: FlightLevel_Unit.STANDARD_METRIC_LEVEL_METRES,
            };
        } else {
            return null;
        }
    }

    static min(fl1: FlightLevel, fl2: FlightLevel) {
        if (FlightLevelUtils.getFeetValue(fl1) <= FlightLevelUtils.getFeetValue(fl2)) return fl1;
        else return fl2;
    }

    static max(fl1: FlightLevel, fl2: FlightLevel) {
        if (FlightLevelUtils.getFeetValue(fl1) >= FlightLevelUtils.getFeetValue(fl2)) return fl1;
        else return fl2;
    }

    /**
     * Compartor that can be given to Array.sort().
     * Returns -1, 0, 1 if level1 is less than, equal to, or greater than level2.
     * @param level1
     * @param level2
     */
    static compare(level1: FlightLevel, level2: FlightLevel): number {
        const sameUnit = level1.unit === level2.unit;
        const v1 = sameUnit ? level1.value : FlightLevelUtils.getFeetValue(level1);
        const v2 = sameUnit ? level2.value : FlightLevelUtils.getFeetValue(level2);

        if (v1 === v2) return 0;
        else if (v1 < v2) return -1;
        else return 1;
    }

    static isUnlimited(level: FlightLevel): boolean {
        return level?.unit === FlightLevel_Unit.FLIGHT_LEVEL && level?.value === FlightLevelUtils.UNLIMITED_VALUE;
    }

    static isOnGround(level: FlightLevel): boolean {
        return level?.value === FlightLevelUtils.ON_GROUND_VALUE;
    }

    static flightLevelDisplayValue(level: FlightLevel) {
        return level ? 'FL' + FlightLevelUtils.getLevelValue(level) : '';
    }

    static contains(band: FlightLevelBand, value: FlightLevel | FlightLevelBand) {
        if (!band || !value) return false;
        if ('unit' in value) {
            return (
                FlightLevelUtils.getLevelValue(band.min) <= FlightLevelUtils.getLevelValue(value) &&
                FlightLevelUtils.getLevelValue(band.max) >= FlightLevelUtils.getLevelValue(value)
            );
        } else {
            const bandValue: FlightLevelBand = value as FlightLevelBand;
            return this.contains(band, bandValue.min) && this.contains(band, bandValue.max);
        }
    }

    static getNumberOfLevels(band: FlightLevelBand) {
        return Math.ceil(
            (FlightLevelUtils.getLevelValue(band.max) - FlightLevelUtils.getLevelValue(band.min)) /
                FlightLevelUtils.NOMINAL_LEVEL_STEP,
        );
    }

    static intersects(band: FlightLevelBand, band2: FlightLevelBand) {
        if (!band || !band2) return false;
        return FlightLevelUtils.compare(band2.max, band.min) >= 0 && FlightLevelUtils.compare(band.max, band2.min) >= 0;
    }

    /**
     * Strict intersection to comply with server side Java code (FlightLevel.intersects)
     * @param band
     * @param band2
     */
    static intersectsExcl(band: FlightLevelBand, band2: FlightLevelBand) {
        if (!band || !band2) return false;
        return FlightLevelUtils.compare(band2.max, band.min) > 0 && FlightLevelUtils.compare(band.max, band2.min) > 0;
    }

    static growToCover(band: FlightLevelBand, level: FlightLevel) {
        if (band.min == null || FlightLevelUtils.getFeetValue(level) < FlightLevelUtils.getFeetValue(band.min)) {
            band.min = level;
        }
        if (band.max == null || FlightLevelUtils.getFeetValue(level) > FlightLevelUtils.getFeetValue(band.max)) {
            band.max = level;
        }
    }
}
